import { useEffect } from 'react'
import {
  Button,
  Form,
  Icon,
  Input,
  InputNumber,
  Modal,
} from 'components/common'
import clsx from 'clsx'
import { ExtensionFee } from 'lib/graphql/storeadmin'
import { CustomizedModalProps } from './types'
import { Divider } from 'antd'
import { useDynamicList } from 'ahooks'

interface Exceed {
  id?: string
  value?: number
  reason?: string
}

interface DynamicFormItemProps {
  getKey: (index: number) => number
  remove: (index: number) => void
  index: number
  initialValue?: Exceed
}

export interface ExceedEditModalProps
  extends Omit<CustomizedModalProps, 'onOk'> {
  exceedValues: ExtensionFee[]
  onOk: (values: ExtensionFee[]) => void
  onClose: (visible: boolean) => void
}

const DynamicFormItem = ({
  index,
  initialValue,
  getKey,
  remove,
}: DynamicFormItemProps) => {
  return (
    <div key={getKey(index)}>
      {index > 0 && <Divider style={{ margin: '0 0 12px' }} />}
      <div className={clsx('grid', 'grid-cols-12')}>
        <div className={clsx('col-span-10')}>
          <Form.Item
            // label="理由"
            rules={[
              {
                required: true,
                message: '理由は必須です。',
              },
              {
                validator: (_, value) => {
                  if (value.length <= 120) {
                    return Promise.resolve()
                  }
                  return Promise.reject(
                    new Error('理由は最大120文字までです。')
                  )
                },
              },
            ]}
            name={['exceeds', getKey(index), 'reason']}
            initialValue={initialValue.reason}
          >
            <Input placeholder="お客様にはこの理由が表示されます。" />
          </Form.Item>
          <Form.Item
            // label="料金"
            rules={[{ required: true, message: '料金は必須です。' }]}
            name={['exceeds', getKey(index), 'value']}
            initialValue={initialValue.value}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={(value) => `￥ ${value}`}
              parser={(value) => value.replace('￥', '')}
            />
          </Form.Item>
        </div>
        <div
          className={clsx(
            'col-span-2',
            'flex',
            'justify-center',
            'items-center'
          )}
        >
          <Form.Item>
            <Button.Group>
              <Button
                danger
                onClick={() => remove(index)}
                icon={<Icon.DeleteFilled />}
              />
            </Button.Group>
          </Form.Item>
        </div>
      </div>
    </div>
  )
}

export const BookingsExceedModal = ({
  exceedValues,
  visible,
  onOk,
  onClose,
  ...reset
}: ExceedEditModalProps) => {
  const [form] = Form.useForm()
  const { list, remove, getKey, push, sortList, resetList } =
    useDynamicList<Exceed>(exceedValues)

  useEffect(() => {
    if (exceedValues) {
      resetList(exceedValues)
    }
  }, [exceedValues, resetList])

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (Object.keys(values).length === 0) {
          onClose(false)
          onOk([])
        } else {
          const sortedResult = sortList(values.exceeds)
          const sortedResultAddId = sortedResult.map((item) => ({
            id: null,
            reason: item.reason,
            value: item.value,
          }))

          onClose(false)
          onOk(sortedResultAddId)
        }
      })
      .catch(console.warn)
  }

  const onCancel = () => {
    // キャンセルが押下された際は、親コンポーネントから受け取った値にリセットする
    resetList(exceedValues)
    onClose(false)
  }

  return (
    <>
      <Modal
        visible={visible}
        title="追加料金の変更"
        onOk={() => onSubmit()}
        onCancel={() => onClose(false)}
        footer={[
          <div>
            <Button key="cancel" onClick={() => onCancel()}>
              キャンセル
            </Button>
            <Button type="primary" key="submit" onClick={() => onSubmit()}>
              完了
            </Button>
          </div>,
        ]}
        {...reset}
      >
        <Form form={form}>
          {list.map((ele, index) => (
            <DynamicFormItem
              key={index}
              getKey={getKey}
              remove={remove}
              index={index}
              initialValue={ele}
            />
          ))}
          <Button
            style={{ marginTop: 10 }}
            block
            type="dashed"
            onClick={() => push({ id: null, value: null, reason: '' })}
          >
            + 追加する
          </Button>
        </Form>
      </Modal>
    </>
  )
}
